import { Checkbox, Flex, Radio, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import React from 'react';

type SelectableItem = {
  id: string;
  title: string;
  description?: string;
  emptyView?: React.ReactNode;
};

type Props<T extends SelectableItem> = {
  items: T[];
  hint?: string;
  selectedItemIds: string[];
  setSelectedItemIds: (itemIds: string[]) => void;
  selectMultiple?: boolean;
  emptyView?: React.ReactNode;
  renderExpandedContent?: (item: T) => React.ReactNode;
};

export default function SelectableItemListWithExpandedContent<T extends SelectableItem>({
  items,
  hint,
  selectedItemIds,
  setSelectedItemIds,
  selectMultiple,
  emptyView,
  renderExpandedContent,
}: Props<T>) {
  const Selector = selectMultiple ? Checkbox : Radio;

  const handleSelectItem = (item: T) => {
    const isSelected = selectedItemIds.includes(item.id);
    if (isSelected) {
      setSelectedItemIds(selectedItemIds.filter(id => id !== item.id));
    } else {
      if (selectMultiple) {
        setSelectedItemIds([...selectedItemIds, item.id]);
      } else {
        setSelectedItemIds([item.id]);
      }
    }
  };

  return (
    <Flex
      direction="column"
      style={{
        borderRadius: 12,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
      }}
      pr={'xxs'}
      mih={0}
    >
      {items.length === 0 && emptyView}
      {items.map(item => {
        const isSelected = selectedItemIds.includes(item.id);
        return (
          <Flex
            key={item.id}
            direction="column"
            gap="xs"
            p="md"
            bd="1px solid var(--mantine-color-gray-1)"
            style={{ borderRadius: 12 }}
            bg="white"
            mb={'xxxs'}
          >
            <Flex direction="column" gap="xs" role={'listitem'}>
              <Flex gap="xs">
                <Selector checked={isSelected} onChange={() => handleSelectItem(item)} mt="xxxs" />
                <Flex flex={1} direction="column" gap="xxxs" onClick={() => handleSelectItem(item)}>
                  <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.7">
                    {item.title}
                  </Text>
                  {item.description && (
                    <Text size="textMd" fw={FONT_WEIGHTS.regular} c="gray.6">
                      {item.description}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Flex>
            {isSelected && renderExpandedContent && renderExpandedContent(item)}
          </Flex>
        );
      })}
      {hint && items.length > 0 && (
        <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" py="xs" px="sm">
          {hint}
        </Text>
      )}
    </Flex>
  );
}
